"use client";
import "../../globals.css";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { login, requestOtp, verifyOtp } from "./actions/actions";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { AuthApiError, AuthError } from "@supabase/supabase-js";
import { createClient } from "@/lib/supabase/client";
import { redirect } from "next/navigation";
import Link from "next/link";

export default function AccessPage() {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(""); // For password login
  const [otp, setOtp] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isNewUser, setIsNewUser] = useState(false);
  const supabase = createClient();
  const [usePassword, setUsePassword] = useState(false); // Toggle between OTP and Password login

  const handleRequestOtp = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    setLoading(true);
    const { data: user, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("email", email)
      .maybeSingle();
    if (error || user == null) {
      setIsNewUser(true);
    } else {
      setIsNewUser(false);
    }
    try {
      let formData = new FormData();
      formData.append("email", email);
      await requestOtp(formData);
      toast({
        title: "OTP Sent",
        description: `Code sent via email to ${email}`,
        variant: "default",
        duration: 5000,
      });
      setStep(1);
    } catch (error) {
      let typedError = error as AuthApiError | AuthError;
      if (typedError.code == "over_email_send_rate_limit") {
        toast({
          title: "Error",
          description:
            "You have exceeded the limit of OTP requests. Please try again later.",
          variant: "destructive",
          duration: 5000,
        });
      } else if (
        typedError.message == "Database error saving new user" &&
        !email.endsWith("@assetplan.co.uk")
      ) {
        toast({
          title: "Invalid domain",
          description:
            "Access is restricted to authorized users only. Please contact the administrator.",
          variant: "destructive",
          duration: 5000,
        });
      } else {
        toast({
          title: "Error",
          description: "An error occured. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLoginWithPassword = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    setLoading(true);
    try {
      let formData = new FormData();
      formData.append("email", email);
      formData.append("password", password);
      await login(formData); // Call the login function for email/password login
      toast({
        title: "Success",
        description: "You have successfully logged in.",
        variant: "default",
        duration: 5000,
      });
    } catch (error) {
      console.log(error);
      let typedError = error as AuthError;
      toast({
        title: "Error",
        description: "An error occurred. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    setLoading(true);
    try {
      let formData = new FormData();
      formData.append("email", email);
      formData.append("otp", otp);
      await verifyOtp(formData, isNewUser);
      if (isNewUser) {
        setStep(2);
      } else {
        toast({
          title: "Success",
          description: "You have successfully logged in.",
          variant: "default",
          duration: 5000,
        });
      }
    } catch (error) {
      console.log(error);
      let typedError = error as AuthError;
      if (typedError.code == "over_email_send_rate_limit") {
        toast({
          title: "Error",
          description:
            "You have exceeded the limit of OTP requests. Please try again later.",
          variant: "destructive",
          duration: 5000,
        });
      } else {
        toast({
          title: "Error",
          description: "An error occurred. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          first_name: firstName,
          last_name: lastName,
        },
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: "You have successfully updated your profile.",
        variant: "default",
        duration: 5000,
      });
      window.location.href = "/";
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid h-screen w-screen place-items-center">
      <div className="lg:p-8">
        <div className="mx-auto flex w-full flex-col justify-center space-y-4 sm:w-[350px]">
          <div className="flex flex-col space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              Welcome to Lumen
            </h1>
            <p className="text-sm text-muted-foreground">
              {step === 0
                ? "Enter your email to access your account"
                : step === 1
                ? "Enter the verification code sent to your email"
                : "Complete your profile information"}
            </p>
          </div>

          <form className="space-y-8" onSubmit={(e) => e.preventDefault()}>
            {step === 0 ? (
              <div className="grid gap-2 space-y-2">
                <div className="grid gap-1">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@example.com"
                      required
                    />
                  </div>
                  {usePassword && (
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="********"
                        required
                      />
                    </div>
                  )}
                </div>
                {!usePassword ? (
                  <Button onClick={handleRequestOtp}>
                    {loading ? (
                      <Loader2 size={12} className="mr-2 animate-spin" />
                    ) : null}
                    Request OTP
                  </Button>
                ) : (
                  <Button onClick={handleLoginWithPassword}>
                    {loading ? (
                      <Loader2 size={12} className="mr-2 animate-spin" />
                    ) : null}
                    Login
                  </Button>
                )}
                <div className="flex justify-center">
                  <Button
                    variant="link"
                    onClick={() => setUsePassword(!usePassword)}
                  >
                    {usePassword
                      ? "Use OTP to login"
                      : "Login with password instead"}
                  </Button>
                </div>
              </div>
            ) : step === 1 ? (
              <div className="grid gap-2 space-y-2">
                <div className="grid">
                  <div className="flex justify-center">
                    <InputOTP
                      maxLength={6}
                      id="otp"
                      name="otp"
                      value={otp}
                      onChange={(e) => setOtp(e)}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                      </InputOTPGroup>
                      <InputOTPSeparator />
                      <InputOTPGroup>
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
                <Button onClick={handleVerifyOtp}>
                  {loading ? (
                    <Loader2 size={12} className="mr-2 animate-spin" />
                  ) : null}
                  Verify OTP
                </Button>
              </div>
            ) : (
              <div className="grid gap-2 space-y-2">
                <div className="grid gap-1">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First Name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last Name"
                      required
                    />
                  </div>
                </div>
                <Button onClick={handleUpdateUser}>
                  {loading ? (
                    <Loader2 size={12} className="mr-2 animate-spin" />
                  ) : null}
                  Save Profile
                </Button>
              </div>
            )}
          </form>
          <p className="px-8 text-center text-sm text-muted-foreground">
            <Link
              href="/login"
              className="hover:text-brand underline underline-offset-4"
            >
              Don&apos;t have an account? Sign Up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
